import { peekPublicUiOptions } from "@Front/Services/PublicSiteConfig/publicSiteConfigLoader";

function usesOperationFolderLexicon(): boolean {
	return peekPublicUiOptions().uses_operation_folder_lexicon;
}

/**
 * Lexique « opération » au lieu de « dossier » lorsque `sites.theme_json.public_ui.uses_operation_folder_lexicon` (via site-config).
 */
export function notaryFolderEntitySingularTitle(): string {
	return usesOperationFolderLexicon() ? "Opération" : "Dossier";
}

export function notaryFolderNavCurrentLabel(): string {
	return usesOperationFolderLexicon() ? "Opérations en cours" : "Dossiers en cours";
}

export function notaryFolderNavArchivedLabel(): string {
	return usesOperationFolderLexicon() ? "Opérations archivées" : "Dossiers archivés";
}

export function notaryFolderLogoListAriaLabel(): string {
	return usesOperationFolderLexicon() ? "Aller à la liste des opérations" : "Aller à la liste des dossiers";
}

const FOLDER_LEXICON_PHRASES_UNSORTED: readonly (readonly [string, string])[] = [
	[
		"Archiver ce dossier le déplacera dans la section des dossiers archivés. Vous pouvez ajouter une note de dossier avant d'archiver si vous le souhaitez.",
		"Archiver cette opération la déplacera dans la section des opérations archivées. Vous pouvez ajouter une note d'opération avant d'archiver si vous le souhaitez.",
	],
	[
		"Choisissez sur la gauche si vous souhaitez vous connecter sur les dossiers de l'étude ou sur les dossiers sur lesquels vous avez été invités par une étude externe.",
		"Choisissez sur la gauche si vous souhaitez vous connecter sur les opérations de l'étude ou sur les opérations sur lesquelles vous avez été invités par une étude externe.",
	],
	[
		"Voulez-vous envoyer ce document à tous les membres du dossier (clients et tiers)",
		"Voulez-vous envoyer ce document à tous les membres de l'opération (clients et tiers)",
	],
	[
		"Vous n'avez pas encore de dossier créé. En quelques clics, commencez un nouveau dossier pour organiser",
		"Vous n'avez pas encore d'opération créée. En quelques clics, commencez une nouvelle opération pour organiser",
	],
	[
		"Cette action retirera le client de ce dossier. Vous ne pourrez plus récupérer les informations associées à ce client dans ce dossier une fois supprimées.",
		"Cette action retirera le client de cette opération. Vous ne pourrez plus récupérer les informations associées à ce client dans cette opération une fois supprimées.",
	],
	[
		"Pour pouvoir faire une demande de document, vous devez d'abord ajouter un ou plusieurs clients à ce dossier.",
		"Pour pouvoir faire une demande de document, vous devez d'abord ajouter un ou plusieurs clients à cette opération.",
	],
	[
		"Invitez un notaire ou un collaborateur d'une autre étude pour travailler sur ce dossier en toute sécurité.",
		"Invitez un notaire ou un collaborateur d'une autre étude pour travailler sur cette opération en toute sécurité.",
	],
	[
		"Obligatoire pour valider l'invitation. Cette adresse est conservée sur le dossier : elle sert aux courriels",
		"Obligatoire pour valider l'invitation. Cette adresse est conservée sur l'opération : elle sert aux courriels",
	],
	[
		"Le chargement a pris trop de temps. Veuillez réessayer ou retourner à la liste des dossiers.",
		"Le chargement a pris trop de temps. Veuillez réessayer ou retourner à la liste des opérations.",
	],
	[
		"Les dossiers supprimés peuvent être restaurés à tout moment. Ils ne sont pas définitivement supprimés.",
		"Les opérations supprimées peuvent être restaurées à tout moment. Elles ne sont pas définitivement supprimées.",
	],
	["Lien de connexion (dossier et étude)", "Lien de connexion (opération et étude)"],
	["des pièces du dossier", "des pièces de l'opération"],
	["des dossiers en cours, archivés ou supprimés.", "des opérations en cours, archivées ou supprimées."],
	["Dossier en lecture seule", "Opération en lecture seule"],
	["accès à ce dossier", "accès à cette opération"],
	["Sélectionner tous les membres du dossier", "Sélectionner tous les membres de l'opération"],
	["Impossible de retirer le notaire invité du dossier.", "Impossible de retirer le notaire invité de l'opération."],
	["Impossible de retirer le tiers du dossier.", "Impossible de retirer le tiers de l'opération."],
	["Impossible de charger le dossier.", "Impossible de charger l'opération."],
	["Impossible de charger votre dossier.", "Impossible de charger votre opération."],
	["La mise à jour des collaborateurs du dossier n'a pas abouti.", "La mise à jour des collaborateurs de l'opération n'a pas abouti."],
	["UID du tiers ou du dossier manquant", "UID du tiers ou de l'opération manquant"],
	["dossier(s) supprimé(s)", "opération(s) supprimée(s)"],
	["Dossiers importés", "Opérations importées"],
	["Liste des dossiers", "Liste des opérations"],
	["Retour aux dossiers", "Retour aux opérations"],
	["Accéder au dossier", "Accéder à l'opération"],
	["Dossiers (par mois)", "Opérations (par mois)"],
	["Dossiers invités", "Opérations invitées"],
	["Dossiers archivés", "Opérations archivées"],
	["dossiers archivés", "opérations archivées"],
	["Dossiers en cours", "Opérations en cours"],
	["dossiers en cours", "opérations en cours"],
	["Dossier archivé", "Opération archivée"],
	["dossier archivé", "opération archivée"],
	["note de dossier", "note d'opération"],
	["Note de dossier", "Note d'opération"],
	["nouveaux dossiers", "nouvelles opérations"],
	["Nouveaux dossiers", "Nouvelles opérations"],
	["nouveau dossier", "nouvelle opération"],
	["Nouveau dossier", "Nouvelle opération"],
	["premier dossier", "première opération"],
	["Premier dossier", "Première opération"],
	["Ce dossier", "Cette opération"],
	["ce dossier", "cette opération"],
	["du dossier", "de l'opération"],
	["Du dossier", "De l'opération"],
	["au dossier", "à l'opération"],
	["Au dossier", "À l'opération"],
	["le dossier", "l'opération"],
	["Le dossier", "L'opération"],
	["un dossier", "une opération"],
	["Un dossier", "Une opération"],
	["des dossiers", "des opérations"],
	["Des dossiers", "Des opérations"],
	["les dossiers", "les opérations"],
	["Les dossiers", "Les opérations"],
	["sur le dossier", "sur l'opération"],
	["Sur le dossier", "Sur l'opération"],
	["dans le dossier", "dans l'opération"],
	["Dans le dossier", "Dans l'opération"],
	["de ce dossier", "de cette opération"],
	["à ce dossier", "à cette opération"],
	["pour ce dossier", "pour cette opération"],
	["sur ce dossier", "sur cette opération"],
	["supprimant ce dossier", "supprimant cette opération"],
	["archiver ce dossier", "archiver cette opération"],
	["supprimer ce dossier", "supprimer cette opération"],
	["désarchiver ce dossier", "désarchiver cette opération"],
	["Intitulé du dossier", "Intitulé de l'opération"],
	["intitulé du dossier", "intitulé de l'opération"],
	["Entrer l'intitulé du dossier", "Entrer l'intitulé de l'opération"],
	["Numéro de dossier", "Numéro d'opération"],
	["numéro de dossier", "numéro d'opération"],
	["Entrer le numéro de dossier", "Entrer le numéro d'opération"],
	["Note du dossier", "Note de l'opération"],
	["Type de dossier", "Type d'opération"],
	["Ouverture du dossier", "Ouverture de l'opération"],
	["membres du dossier", "membres de l'opération"],
	["contenu du dossier client", "contenu de l'opération client"],
	["contenu du dossier", "contenu de l'opération"],
	["entrée dans le dossier", "entrée dans l'opération"],
	["dans la section des dossiers archivés", "dans la section des opérations archivées"],
	["configuration du dossier", "configuration de l'opération"],
	["solliciter de nouveaux documents sur ce dossier", "solliciter de nouveaux documents sur cette opération"],
	["gestion des acteurs tiers pour ce dossier", "gestion des acteurs tiers pour cette opération"],
	["partager ce dossier avec", "partager cette opération avec"],
	["consulter ce dossier", "consulter cette opération"],
	["Inviter un confrère au dossier", "Inviter un confrère à l'opération"],
	["Ajouter un acteur tiers au dossier", "Ajouter un acteur tiers à l'opération"],
	["Ajouter des clients au dossier", "Ajouter des clients à l'opération"],
	["Partager le dossier avec un confrère", "Partager l'opération avec un confrère"],
	["supprimer ce client du dossier", "supprimer ce client de l'opération"],
	["supprimer le client du dossier", "supprimer le client de l'opération"],
	["Impossible de supprimer le dossier", "Impossible de supprimer l'opération"],
	["pendant la création du dossier.", "pendant la création de l'opération."],
	["Désarchiver le dossier ?", "Désarchiver l'opération ?"],
	["Désarchiver le dossier", "Désarchiver l'opération"],
	["désarchivage du dossier", "désarchivage de l'opération"],
	["Erreur lors du désarchivage du dossier:", "Erreur lors du désarchivage de l'opération:"],
	["Êtes-vous sûr de vouloir désarchiver ce dossier ?", "Êtes-vous sûr de vouloir désarchiver cette opération ?"],
	["Le dossier a été désarchivé avec succès.", "L'opération a été désarchivée avec succès."],
	["créer un dossier", "créer une opération"],
	["Créer un dossier", "Créer une opération"],
	["Créer votre premier dossier", "Créer votre première opération"],
	["Archiver le dossier", "Archiver l'opération"],
	["Supprimer le dossier", "Supprimer l'opération"],
	["Sélectionner un dossier", "Sélectionner une opération"],
	["Chargement du dossier", "Chargement de l'opération"],
	["données du dossier", "données de l'opération"],
	["participants de ce dossier", "participants de cette opération"],
	["ajouter des tiers sur ce dossier", "ajouter des tiers sur cette opération"],
	["Aucun dossier créé", "Aucune opération créée"],
	["vérifier la configuration du dossier", "vérifier la configuration de l'opération"],
	["Complétion du dossier client", "Complétion de l'opération client"],
	["Fiche membres du dossier", "Fiche membres de l'opération"],
	["Informations du dossier", "Informations de l'opération"],
	["Aucun dossier sélectionné", "Aucune opération sélectionnée"],
	["Vos dossiers en cours", "Vos opérations en cours"],
	["Veuillez sélectionner un dossier à gauche.", "Veuillez sélectionner une opération à gauche."],
	[
		"Veuillez sélectionner le dossier pour lequel vous souhaitez déposer ou consulter des documents.",
		"Veuillez sélectionner l'opération pour laquelle vous souhaitez déposer ou consulter des documents.",
	],
	["créer de dossier", "créer d'opération"],
	["Créer de dossier", "Créer d'opération"],
];

const FOLDER_LEXICON_PHRASES_LONGEST_FIRST: readonly (readonly [string, string])[] = [...FOLDER_LEXICON_PHRASES_UNSORTED].sort(
	(a, b) => b[0].length - a[0].length,
);

const FOLDER_LEXICON_AGREEMENT_FIXES: readonly (readonly [RegExp, string])[] = [
	[/\bOpérations invités\b/g, "Opérations invitées"],
	[/\bopérations invités\b/g, "opérations invitées"],
	[/\bOpérations archivés\b/g, "Opérations archivées"],
	[/\bopérations archivés\b/g, "opérations archivées"],
	[/\bOpération archivés\b/g, "Opération archivée"],
	[/\bopération archivés\b/g, "opération archivée"],
	[/\bOpérations importés\b/g, "Opérations importées"],
	[/\bopérations importés\b/g, "opérations importées"],
];

export function applyFolderAsOperationFr(text: string): string {
	if (!usesOperationFolderLexicon()) {
		return text;
	}
	let t = text;
	for (const [from, to] of FOLDER_LEXICON_PHRASES_LONGEST_FIRST) {
		if (from.length === 0) {
			continue;
		}
		t = t.split(from).join(to);
	}
	t = t
		.replace(/\bDossiers\b/g, "Opérations")
		.replace(/\bdossiers\b/g, "opérations")
		.replace(/\bDossier\b/g, "Opération")
		.replace(/\bdossier\b/g, "opération");
	for (const [pattern, replacement] of FOLDER_LEXICON_AGREEMENT_FIXES) {
		t = t.replace(pattern, replacement);
	}
	return t;
}
